import { useEffect, useRef } from 'react'

import type { IAnnualProgram } from '../../../../types/annual-programs'

interface UseProgramSwitcherProps {
	programs: IAnnualProgram[]
	onProgramChange: (index: number) => void
}

/**
 * Custom hook that handles program switching and scrolling via data attributes
 * 
 * Supports:
 * - data-program-id="123" - switches to program with id=123 or index=123
 * - data-scroll-to="section-id" - scrolls to element with id="section-id"
 * - Both attributes can be used together on the same element
 * 
 * @example
 * <button data-program-id="1" data-scroll-to="kupit-program">
 *   Switch to program 1 and scroll
 * </button>
 */
export function useProgramSwitcher({ programs, onProgramChange }: UseProgramSwitcherProps) {
	const programsRef = useRef<IAnnualProgram[]>([])
	const onProgramChangeRef = useRef(onProgramChange)

	programsRef.current = programs
	onProgramChangeRef.current = onProgramChange

	useEffect(() => {
		const handleClick = (e: MouseEvent): void => {
			// Ignore clicks inside switcher (to prevent duplicate handling)
			if ((e.target as Element).closest?.('.m-annual-program-preview__switcher')) {
				return
			}

			// Find element with data-program-id or data-scroll-to
			const el = (e.target as Element).closest?.(
				'[data-program-id], [data-scroll-to]'
			) as HTMLElement | null

			if (!el) return

			const list = programsRef.current
			let programSwitched = false

			// Handle data-program-id
			const programIdAttr = el.getAttribute('data-program-id')
			if (programIdAttr != null) {
				const n = parseInt(programIdAttr, 10)
				if (!Number.isNaN(n) && list.length > 0) {
					// Try to find by program id first
					const byId = list.findIndex(p => p.id === n)
					if (byId >= 0) {
						onProgramChangeRef.current(byId)
						programSwitched = true
					} else if (n >= 0 && n < list.length) {
						// Otherwise use as index
						onProgramChangeRef.current(n)
						programSwitched = true
					}
				}
			}

			// Handle data-scroll-to
			const scrollToAttr = el.getAttribute('data-scroll-to')
			if (scrollToAttr) {
				// If program was switched, delay scroll to allow re-render
				const delay = programSwitched ? 100 : 0
				setTimeout(() => {
					const targetSection = document.getElementById(scrollToAttr)
					if (targetSection) {
						targetSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
					}
				}, delay)
			}
		}

		document.addEventListener('click', handleClick)
		return () => document.removeEventListener('click', handleClick)
	}, [])
}
